import { useToastNotificationsStore } from "@shared/stores/toastNotifications";
import {
  NotificationOptions,
  ToastNotificationCtrl,
} from "@shared/types/localTypes/toastNotifications.ts";
import logger from "@shared/utils/logger.ts";
import { useSessionLogic } from "@shared/use/useSessionLogic.ts";
import {
  jfToastEmitter,
  type NotificationType,
} from "@/use/useJfToastEmitter.ts";

export const useNotifications: ToastNotificationCtrl = () => {
  const toastNotificationsStore = useToastNotificationsStore();
  const sessionLogic = useSessionLogic();

  const success = (message: string, options?: NotificationOptions) => {
    return notify("success", message, options);
  };

  const warning = (message: string, options?: NotificationOptions) => {
    return notify("warning", message, options);
  };

  const error = (message: string, options?: NotificationOptions) => {
    return notify("error", message, options);
  };

  const info = (message: string, options?: NotificationOptions) => {
    return notify("info", message, options);
  };

  const closeAll = async () => {
    jfToastEmitter.emit("closeAll");
  };

  const notify = async (
    type: NotificationType,
    message: string,
    options?: NotificationOptions,
  ) => {
    if (!canDisplay(type, message, options)) {
      return;
    }
    if (isAlreadyVisible(type, message)) {
      return;
    }

    const key = `${Date.now()}${Math.random()}`;

    jfToastEmitter.emit("show", {
      type,
      config: {
        detail: message,
        allowHtml: true,
        life: options?.duration,
        onClose: () => {
          toastNotificationsStore.removeVisibleNotification(key);
        },
      },
    });

    toastNotificationsStore.addVisibleNotification({
      key,
      type,
      title: type,
      message,
    });
  };

  const canDisplay = (
    type: NotificationType,
    message: string,
    options?: NotificationOptions,
  ): boolean => {
    if (type !== "error") {
      return true;
    }
    if (toastNotificationsStore.errorLocked && !options?.forceDisplay) {
      return false;
    }
    if (!sessionLogic.isSessionActive()) {
      logger.error("notification error fallback : " + message);
      return false;
    }
    return true;
  };

  const isAlreadyVisible = (
    type: NotificationType,
    message: string,
  ): boolean => {
    return toastNotificationsStore.isNotificationVisible({
      type,
      title: type,
      message,
    });
  };

  return { success, warning, error, info, closeAll };
};
